import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
         ReferenceLine } from 'recharts';
import FilterBar from './shared/FilterBar';
import './AbilityPerformance.css';

const CHART_COLORS = [
  '#2196F3', '#FF5722', '#4CAF50', '#9C27B0', '#FF9800',
  '#00BCD4', '#E91E63', '#8BC34A', '#3F51B5', '#FFEB3B',
  '#009688', '#F44336', '#03A9F4', '#CDDC39', '#673AB7',
  '#FFC107', '#607D8B', '#795548', '#00E676', '#FF6D00',
];

const METRIC_CONFIGS = [
  { key: 'fpsAvg', label: 'FPS', unit: 'fps', refLine: 60 },
  { key: 'gpuTimeAvg', label: 'GPU Frame Time', unit: 'ms', refLine: null },
  { key: 'cpuMainTimeAvg', label: 'CPU Main Frame Time', unit: 'ms', refLine: null },
  { key: 'cpuRenderTimeAvg', label: 'CPU Render Frame Time', unit: 'ms', refLine: null },
];

const CATEGORY_ORDER = ['Legends', 'Bosses', 'Essence Bursts', 'All Characters', 'Other'];

/**
 * Ability Performance tab.
 * Shows per-ability FPS/GPU/CPU metrics over CLs, grouped by category.
 */
function AbilityPerformance({ api, filters, onFiltersChange }) {
  const [abilityTests, setAbilityTests] = useState([]);
  const [selectedTest, setSelectedTest] = useState(null);
  const [perfData, setPerfData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [testsLoading, setTestsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState('All');

  const [resolved, setResolved] = useState(null);
  const handleResolvedFilters = useCallback((r) => setResolved(r), []);

  // Fetch available ability tests
  useEffect(() => {
    if (!resolved) return;
    let cancelled = false;

    async function fetchTests() {
      setTestsLoading(true);
      try {
        const result = await api.getAbilityTests(
          resolved.effectiveDays, resolved.filters,
          resolved.startDate, resolved.endDate
        );
        if (!cancelled) {
          setAbilityTests(result.data || []);
          // Auto-select first test if none selected
          if (!selectedTest && result.data?.length > 0) {
            setSelectedTest(result.data[0].testName);
          }
        }
      } catch (err) {
        console.warn('Failed to load ability tests:', err.message);
      } finally {
        if (!cancelled) setTestsLoading(false);
      }
    }

    fetchTests();
    return () => { cancelled = true; };
  }, [api, resolved]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch performance data for selected test
  useEffect(() => {
    if (!resolved || !selectedTest) return;
    let cancelled = false;

    async function fetchPerf() {
      setLoading(true);
      setError(null);
      try {
        const result = await api.getAbilityPerformance(
          selectedTest, resolved.effectiveDays, resolved.filters,
          resolved.startDate, resolved.endDate
        );
        if (!cancelled) setPerfData(result.data || []);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchPerf();
    return () => { cancelled = true; };
  }, [api, resolved, selectedTest]);

  // Group tests by category
  const groupedTests = useMemo(() => {
    const groups = {};
    for (const test of abilityTests) {
      if (!groups[test.category]) groups[test.category] = [];
      groups[test.category].push(test);
    }
    // Sort by predefined order
    const sorted = {};
    for (const cat of CATEGORY_ORDER) {
      if (groups[cat]) sorted[cat] = groups[cat];
    }
    // Add any remaining
    for (const cat of Object.keys(groups)) {
      if (!sorted[cat]) sorted[cat] = groups[cat];
    }
    return sorted;
  }, [abilityTests]);

  // Filter tests by selected category
  const visibleTests = useMemo(() => {
    if (categoryFilter === 'All') return abilityTests;
    return abilityTests.filter(t => t.category === categoryFilter);
  }, [abilityTests, categoryFilter]);

  const availableCategories = useMemo(() => {
    const cats = new Set(abilityTests.map(t => t.category));
    return ['All', ...CATEGORY_ORDER.filter(c => cats.has(c))];
  }, [abilityTests]);

  // Transform perf data into chart format per metric
  const { chartDataByMetric, contexts } = useMemo(() => {
    if (!perfData.length) return { chartDataByMetric: {}, contexts: [] };

    const ctxSet = new Set();
    perfData.forEach(r => ctxSet.add(r.context));
    const contexts = Array.from(ctxSet).sort();

    const chartDataByMetric = {};
    for (const mc of METRIC_CONFIGS) {
      const clMap = new Map();
      for (const row of perfData) {
        if (!clMap.has(row.changelist)) {
          clMap.set(row.changelist, { changelist: row.changelist });
        }
        const val = row[mc.key];
        if (val != null) {
          clMap.get(row.changelist)[row.context] = Math.round(val * 100) / 100;
        }
      }
      chartDataByMetric[mc.key] = Array.from(clMap.values());
    }

    return { chartDataByMetric, contexts };
  }, [perfData]);

  const selectedTestInfo = abilityTests.find(t => t.testName === selectedTest);

  return (
    <div className="ability-perf">
      <div className="ability-perf-header">
        <h2>Ability Performance</h2>
        <p className="ability-perf-description">
          Per-ability FPS, GPU, and CPU metrics across changelists.
          Select a legend, boss, or essence burst test to view its performance over time.
        </p>
      </div>

      <div className="ability-perf-controls">
        <FilterBar
          api={api}
          filters={filters}
          onFiltersChange={onFiltersChange}
          onResolvedFilters={handleResolvedFilters}
          showTestFilter={false}
        />
      </div>

      <div className="ability-perf-layout">
        {/* Test selector sidebar */}
        <div className="ability-perf-sidebar">
          <div className="ability-perf-category-filter">
            {availableCategories.map(cat => (
              <button
                key={cat}
                className={`category-btn ${categoryFilter === cat ? 'active' : ''}`}
                onClick={() => setCategoryFilter(cat)}
              >
                {cat}
              </button>
            ))}
          </div>
          <div className="ability-perf-test-list">
            {testsLoading ? (
              <div className="ability-perf-loading">Loading tests...</div>
            ) : visibleTests.length === 0 ? (
              <div className="ability-perf-empty">No ability tests found.</div>
            ) : (
              categoryFilter === 'All' ? (
                // Grouped view
                Object.entries(groupedTests).map(([category, tests]) => (
                  <div key={category} className="test-group">
                    <div className="test-group-header">{category}</div>
                    {tests.map(test => (
                      <button
                        key={test.testName}
                        className={`test-item ${selectedTest === test.testName ? 'active' : ''}`}
                        onClick={() => setSelectedTest(test.testName)}
                      >
                        <span className="test-name">{test.displayName}</span>
                        <span className="test-count">{test.recordCount}</span>
                      </button>
                    ))}
                  </div>
                ))
              ) : (
                // Flat view for single category
                visibleTests.map(test => (
                  <button
                    key={test.testName}
                    className={`test-item ${selectedTest === test.testName ? 'active' : ''}`}
                    onClick={() => setSelectedTest(test.testName)}
                  >
                    <span className="test-name">{test.displayName}</span>
                    <span className="test-count">{test.recordCount}</span>
                  </button>
                ))
              )
            )}
          </div>
        </div>

        {/* Charts area */}
        <div className="ability-perf-charts">
          {!selectedTest ? (
            <div className="ability-perf-empty">Select a test from the sidebar.</div>
          ) : error ? (
            <div className="ability-perf-error">Failed to load data: {error}</div>
          ) : loading ? (
            <div className="ability-perf-loading">Loading performance data...</div>
          ) : perfData.length === 0 ? (
            <div className="ability-perf-empty">No data for this test in the selected date range.</div>
          ) : (
            <>
              <h3 className="ability-perf-chart-title">
                {selectedTestInfo?.displayName || selectedTest}
                <span className="ability-perf-chart-subtitle">
                  {contexts.length} context{contexts.length !== 1 ? 's' : ''} across {chartDataByMetric.fpsAvg?.length || 0} CLs
                </span>
              </h3>
              {METRIC_CONFIGS.map(mc => (
                <div key={mc.key} className="ability-perf-chart-section">
                  <h4>{selectedTestInfo?.displayName || selectedTest} - {mc.label}</h4>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={chartDataByMetric[mc.key] || []} margin={{ top: 10, right: 30, left: 10, bottom: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                      <XAxis
                        dataKey="changelist"
                        tick={{ fontSize: 11 }}
                        angle={-45}
                        textAnchor="end"
                        height={70}
                        interval="preserveStartEnd"
                      />
                      <YAxis
                        tick={{ fontSize: 12 }}
                        label={{ value: mc.unit, angle: -90, position: 'insideLeft', style: { fontSize: 12 } }}
                      />
                      <Tooltip
                        contentStyle={{ fontSize: 12, maxHeight: 250, overflowY: 'auto' }}
                        labelFormatter={(cl) => `CL ${cl}`}
                        formatter={(value) => [value?.toFixed(1), '']}
                      />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      {contexts.map((ctx, i) => (
                        <Line
                          key={ctx}
                          type="monotone"
                          dataKey={ctx}
                          stroke={CHART_COLORS[i % CHART_COLORS.length]}
                          dot={false}
                          strokeWidth={1.5}
                          connectNulls
                        />
                      ))}
                      {mc.refLine && (
                        <ReferenceLine y={mc.refLine} stroke="#f44336" strokeDasharray="5 5" label={`${mc.refLine} ${mc.unit}`} />
                      )}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default AbilityPerformance;
